import { Directive, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Formats numeric inputs with Italian style grouping and decimals.
 * - Displays dot as thousands separator and comma as decimal separator
 * - Always shows 2 decimals on blur (e.g., 1 -> 1,00; 1,5 -> 1,50)
 * - Stores the control value as a formatted string; convert back to number at save time in component code.
 */
@Directive({
  selector: '[itnumberformat],[itNumberFormat]'
})
export class ItNumberFormatDirective implements OnInit, OnDestroy {
  private isFocused = false;
  private sub?: Subscription;

  constructor(private el: ElementRef<HTMLInputElement>, private ngControl: NgControl) {}

  ngOnInit(): void {
    // Format initial value if present
    const ctrl = this.ngControl?.control;
    const val = ctrl?.value;
    if (val !== null && val !== undefined && val !== '') {
      const num = this.toNumber(val);
      const formatted = this.formatNumber(num);
      this.setDisplay(formatted);
      // Ensure control stores formatted string
      ctrl?.setValue(formatted, { emitEvent: false });
    }

    // Keep display in sync with control changes when not focused
    const c = this.ngControl?.control;
    if (c) {
      this.sub = c.valueChanges.subscribe(v => {
        if (!this.isFocused) {
          const num = this.toNumber(v);
          const display = Number.isFinite(num) ? this.formatNumber(num) : '';
          this.setDisplay(display);
          // If control holds a number, convert it back to formatted string to keep consistency
          if (typeof v === 'number' && Number.isFinite(num)) {
            c.setValue(display, { emitEvent: false });
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @HostListener('focus')
  onFocus(): void {
    this.isFocused = true;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let raw = input.value || '';
    // Keep only digits, dots, commas
    raw = raw.replace(/[^0-9.,]/g, '');

    // Normalize: remove all grouping dots
    const noGroup = raw.replace(/\./g, '');

    // Determine typed decimal and its length (0..2) using comma
    const decMatch = noGroup.match(/,(\d{0,2})$/);
    const typedDec = decMatch ? decMatch[1] : '';

    // Build a numeric from normalized string (replace comma with dot)
    const normalized = noGroup.replace(',', '.');
    const num = parseFloat(normalized);

    // Real-time display formatting: group thousands and keep the typed decimal length
    const minF = typedDec === '' ? 0 : typedDec.length;
    const maxF = typedDec === '' ? 0 : typedDec.length;
    let display = Number.isFinite(num)
      ? this.formatNumber(num, { useGrouping: true, minFraction: minF, maxFraction: maxF })
      : '';
    // Preserve trailing comma if user just typed it
    if (/,\s*$/.test(noGroup) && display) {
      display = display + ',';
    }
    this.setDisplay(display);
    // Store formatted string into control
    const ctrl = this.ngControl?.control;
    ctrl?.setValue(display, { emitEvent: true });
  }

  @HostListener('blur')
  onBlur(): void {
    this.isFocused = false;
    const ctrl = this.ngControl?.control;
    const val = ctrl?.value;
    if (val === null || val === undefined || val === '') {
      this.setDisplay('');
      return;
    }
    const num = this.toNumber(val);
    // Always show 2 decimals and grouping
    const formatted = this.formatNumber(num, { useGrouping: true, minFraction: 2, maxFraction: 2 });
    this.setDisplay(formatted);
    // Keep control value as formatted string
    ctrl?.setValue(formatted, { emitEvent: false });
  }

  private setDisplay(val: string): void {
    this.el.nativeElement.value = val;
  }

  private toNumber(value: any): number {
    if (typeof value === 'number') return value;
    if (value === null || value === undefined) return NaN;
    const s = String(value).trim();
    if (!s) return NaN;
    // Heuristics:
    // - If there is a comma, assume Italian: remove grouping dots and convert comma to dot.
    // - Without commas, analyse dots: treat them as thousands separators when they look like grouped triples,
    //   otherwise fall back to considering the dot a decimal separator.
    let normalized = s;
    if (s.includes(',')) {
      normalized = s.replace(/\./g, '').replace(',', '.');
    } else {
      // No comma: decide whether dots are grouping or decimal separators
      const sanitized = s.replace(/\s+/g, '');
      const dotMatches = sanitized.match(/\./g);
      const dotCount = dotMatches ? dotMatches.length : 0;

      if (dotCount === 0) {
        normalized = sanitized;
      } else {
        const parts = sanitized.split('.');
        const integerPart = parts[0];
        const fractionalPart = parts[parts.length - 1];
        const looksLikeThousandGrouping =
          integerPart.length > 0 &&
          integerPart.length <= 3 &&
          parts.slice(1).every(group => group.length === 3);
        const looksLikeDecimalSeparator = dotCount === 1 && fractionalPart.length > 0 && fractionalPart.length <= 2;

        if (looksLikeThousandGrouping && !looksLikeDecimalSeparator) {
          normalized = sanitized.replace(/\./g, '');
        } else {
          normalized = sanitized;
        }
      }
    }
    const n = parseFloat(normalized);
    return isNaN(n) ? NaN : n;
  }

  private formatNumber(n: number, opts?: { useGrouping?: boolean; minFraction?: number; maxFraction?: number }): string {
    if (n === null || n === undefined || isNaN(n as any)) return '';
    const useGrouping = opts?.useGrouping !== false;
    const minF = opts?.minFraction ?? 2;
    const maxF = opts?.maxFraction ?? 2;
    // Use Intl with Italian locale to respect separators
    const formatter = new Intl.NumberFormat('it-IT', {
      useGrouping,
      minimumFractionDigits: minF,
      maximumFractionDigits: maxF,
    });
    return formatter.format(n);
  }
}
